/**
 * @file    ColorSection.tsx
 * @scope   VIEW
 * @summary Colour picker of the drawer main view: a single colour or a colour family, depending on
 *          what the selected chart supports.
 * @consumes DrawerContext (customWidget), chartCapabilities strategy tables
 * @produces JSX only, or `null` when the chart supports neither
 * @sideEffects None — selections are propagated through the injected callbacks.
 * @notes Strategy tables (`chartsWithColor` / `chartsWithColorFamilies`) decide which control to
 *        render, so adding a chart never requires touching this file.
 */
import React from 'react';
import ColorInput from 'core-ui/ColorInput/ColorInput';
import ColorFamilyInput from 'core-ui/ColorFamilyInput/ColorFamilyInput';
import {
  chartsWithColor,
  chartsWithColorFamilies,
} from '../../../common/constants/chartCapabilities';
import { useDrawerContext } from '../../../State/DrawerContext';

export interface ColorSectionProps {
  /** Resolves the colour currently stored for the given series index. */
  initColor: (index: number) => any;
  /** Resolves the colour family currently stored on the widget. */
  initColorFamily: () => any;
  onSelectColor: (color: any) => void;
  onSelectColorFamily: (colorFamily: any) => void;
}

const ColorSection: React.FC<ColorSectionProps> = ({
  initColor,
  initColorFamily,
  onSelectColor,
  onSelectColorFamily,
}) => {
  const { customWidget } = useDrawerContext();

  if (chartsWithColor.includes(customWidget.alias)) {
    return (
      <div className="diy-drawer-section">
        <ColorInput
          inputTitle={'Color'}
          index={0}
          defaultColor={initColor(0)}
          setSelectedColor={onSelectColor}
        />
      </div>
    );
  }

  if (chartsWithColorFamilies.includes(customWidget.alias)) {
    return (
      <div className="diy-drawer-section">
        <ColorFamilyInput
          inputTitle={'Color Families'}
          defaultColorFamily={initColorFamily()}
          selectColorFamily={onSelectColorFamily}
        />
      </div>
    );
  }

  return null;
};

export default React.memo(ColorSection);
